import React from 'react';
import PropTypes from 'prop-types';
import { useFormContext, Controller } from 'react-hook-form';
import { Link } from 'react-router-dom';
import _isEmpty from 'lodash/isEmpty';

import { parseNumber } from 'helpers/core';
import InlineSVG from 'react-inlinesvg';
import { bem } from 'helpers/class-name';
import { ICONS } from 'constants/icons';
import { intlMessage, manageServicesAvailabilityShape } from 'constants/propTypes';
import Message from 'components/Message';
import When from 'components/When';
import { AvailabilitySummary } from '../../../Components/AvailabilitySummary';
import { messages } from '../../Services.messages';
import './BookablePicker.scss';

const { block, element } = bem('bookable-picker');

export const BookablePickerCheckboxes = React.forwardRef(({ value = [], onChange, bookables, editItemLink }, ref) => {
  const handleChange = event => {
    const targetValue = parseNumber(event.target.value);
    const update = value.includes(targetValue) ? value.filter(id => id !== targetValue) : value.concat(targetValue);
    onChange(update);
  };

  return (
    <>
      {(bookables || []).map(bookable => {
        const { id: bookableId, name: bookableName, recurrence } = bookable;
        return (
          <div key={bookableId} className={element('row', 'grid', 'grid--flush')}>
            <div className="grid__item 2/5 pl">
              <input
                type="checkbox"
                id={bookableId}
                value={bookableId}
                onChange={handleChange}
                checked={value.includes(bookableId)}
                ref={ref}
              />
              <label htmlFor={bookableId}>
                <span>{bookableName}</span>
              </label>
            </div>
            <Link className={element('availability-link', 'grid__item 3/5')} to={editItemLink(bookableId)}>
              <div className={element('recurrence_cell')}>
                <AvailabilitySummary recurrence={recurrence} isInline />
                <InlineSVG src={ICONS.rightArrow} />
              </div>
            </Link>
          </div>
        );
      })}
    </>
  );
});

BookablePickerCheckboxes.propTypes = {
  value: PropTypes.arrayOf(PropTypes.number),
  onChange: PropTypes.func,
  bookables: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      recurrence: manageServicesAvailabilityShape.recurrence,
    })
  ),
  editItemLink: PropTypes.func,
};

const BookablePicker = ({
  name,
  bookables,
  titleMessage,
  subtitleMessage,
  addElementLink,
  addElementMessage,
  editItemLink,
}) => {
  const { control } = useFormContext();

  return (
    <div className={block()}>
      <Message as="p" message={titleMessage} className="text--medium text--semibold" />
      <Message as="p" message={subtitleMessage} />

      <When predicate={!_isEmpty(bookables)}>
        <div className={element('row', 'grid', 'grid--flush')}>
          <Message as="div" className="grid__item 2/5 text--light pl" message={messages.form.bookableName} />
          <Message as="div" className="grid__item 3/5 text--light" message={messages.form.bookableAvailability} />
        </div>

        <Controller
          control={control}
          name={name}
          render={({ field }) => (
            <BookablePickerCheckboxes {...field} bookables={bookables} editItemLink={editItemLink} />
          )}
        />
      </When>
      <Message
        as="p"
        className="mt+"
        message={addElementMessage}
        values={{ a: text => <Link to={addElementLink}>{text}</Link> }}
      />
    </div>
  );
};

BookablePicker.propTypes = {
  name: PropTypes.string,
  bookables: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      recurrence: manageServicesAvailabilityShape.recurrence,
    })
  ),
  titleMessage: intlMessage,
  subtitleMessage: intlMessage,
  addElementLink: PropTypes.string,
  addElementMessage: intlMessage,
  editItemLink: PropTypes.func,
};

export default BookablePicker;
